import { Controller } from '@hotwired/stimulus';
import { Helpers } from '../../helpers.js';

export default class extends Controller {
  static targets = [
    'form',
    'selector',
    'amountInput',
    'amountInputErrorMessage',
    'button',
    'buttonTooltip',
    'selectedInvestor',
    'subscriptionAmount',
    'displayAmount',
    'investorInstruction',
  ];

  connect() {
    this.updateModalInvestor();
    this.updateModalAmount();
    this.updateInvestorInstruction();
    this.toggleSubscribeButton();
  }

  preventEnterOnForm(event) {
    if (event.key !== 'Enter') return;

    // Let Enter activate buttons and links (keyboard accessibility)
    if (event.target.closest("button, a, [role='button']")) return;

    event.preventDefault();
  }

  handleSelectorChange() {
    this.updateModalInvestor();
    this.updateInvestorInstruction();
    this.toggleSubscribeButton();
  }

  handleSubscriptionChange() {
    const amount = parseInt(this.amountInputTarget.value, 10);
    const projectShareValue = this.amountInputTarget.dataset.projectShareValue;
    const errorTarget = this.amountInputErrorMessageTarget;

    // The multiple rule comes first: checkValidity folds stepMismatch and the bounds into one boolean, so a
    // non-multiple would otherwise get the bounds message, which says nothing about its problem.
    if (isNaN(amount)) {
      this.hideAmountError();
    } else if (!Helpers.numberIsMultipleOf(amount, projectShareValue)) {
      const [previousMultiple, nextMultiple] = Helpers.closestMultiplesOf(amount, projectShareValue);

      this.#showAmountError(
        errorTarget.dataset.invalidAmountMessage
          .replace('{previous_multiple}', Helpers.numberToCurrency(previousMultiple, 2))
          .replace('{next_multiple}', Helpers.numberToCurrency(nextMultiple, 2))
      );
    } else if (!this.amountInputTarget.checkValidity()) {
      this.#showAmountError(errorTarget.dataset.outOfRangeMessage);
    } else {
      this.hideAmountError();
    }

    this.updateModalAmount();
    this.updateInvestorInstruction();
    this.toggleSubscribeButton();
  }

  #showAmountError(message) {
    this.amountInputErrorMessageTarget.textContent = message;
    this.amountInputErrorMessageTarget.classList.remove('d-none');
  }

  hideAmountError() {
    this.amountInputErrorMessageTarget.classList.add('d-none');
    this.amountInputErrorMessageTarget.textContent = '';
  }

  updateModalInvestor() {
    if (!this.hasSelectorTarget || !this.hasSelectedInvestorTarget) return;

    this.selectedInvestorTarget.textContent = this.selectorTarget.dataset.investorName || '';
  }

  // Also called on connect: a browser back restores the field values without firing any input event.
  updateModalAmount() {
    if (!this.hasAmountInputTarget || !this.hasSubscriptionAmountTarget) return;

    const amount = parseInt(this.amountInputTarget.value, 10);

    this.subscriptionAmountTarget.textContent = isNaN(amount) ? '' : Helpers.numberToCurrency(amount, 2);

    if (this.hasDisplayAmountTarget) {
      this.displayAmountTarget.textContent = isNaN(amount) ? '' : Helpers.numberToCurrency(amount, 2);
    }
  }

  updateInvestorInstruction() {
    if (!this.hasInvestorInstructionTarget || !this.hasSelectorTarget) return;

    const instruction = this.investorInstructionTarget;
    const balanceInCents = parseInt(this.selectorTarget.dataset.investorBalanceCents, 10);
    const amount = this.hasAmountInputTarget ? parseInt(this.amountInputTarget.value, 10) : NaN;
    const funded = !isNaN(balanceInCents) && !isNaN(amount) && balanceInCents >= amount * 100;

    instruction.textContent = funded
      ? instruction.dataset.fundedMessage
      : instruction.dataset.unfundedMessage;
  }

  toggleSubscribeButton() {
    if (!this.hasButtonTarget || !this.hasSelectorTarget || !this.hasAmountInputTarget) return;

    const hasSelectedInvestor = this.selectorTarget.value !== '';
    const amount = this.amountInputTarget.value;
    const projectShareValue = this.amountInputTarget.dataset.projectShareValue;

    this.buttonTarget.disabled = !(
      hasSelectedInvestor &&
      Helpers.numberIsMultipleOf(amount, projectShareValue) &&
      // `max` carries the envelope balance: delegating to the field keeps that ceiling in a single place.
      this.amountInputTarget.checkValidity()
    );

    this.#toggleInvestorTooltip(hasSelectedInvestor);
  }

  // The amount gates carry their own message under the field; only the missing investor has none.
  #toggleInvestorTooltip(hasSelectedInvestor) {
    if (!this.hasButtonTooltipTarget) return;

    const tooltip = bootstrap.Tooltip.getOrCreateInstance(this.buttonTooltipTarget);

    if (hasSelectedInvestor) {
      tooltip.disable();
      tooltip.hide();
    } else {
      tooltip.enable();
    }
  }
}
